import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "@/hooks/use-toast";
import { getPlanByProductId } from "@/lib/stripe-plans";

export interface OnboardingStep {
  slug: string;
  title: string;
  description: string;
  icon: string;
  route: string | null;
  visibleFor?: "all" | "plus_pro";
}

const ALL_STEPS: OnboardingStep[] = [
  {
    slug: "account_created",
    title: "Conta criada com sucesso",
    description: "Seu cadastro está ativo e pronto para uso.",
    icon: "CheckCircle2",
    route: null,
  },
  {
    slug: "members",
    title: "Monte seu time",
    description: "Adicione os atendentes e administradores que vão gerenciar a operação no FabChat ao seu lado.",
    icon: "Users",
    route: "/settings",
    visibleFor: "plus_pro",
  },
  {
    slug: "leads",
    title: "Organize seus contatos",
    description: "Importe sua base atual de clientes para que o sistema e a IA já comecem a reconhecer com quem estão falando.",
    icon: "Contact",
    route: "/leads",
  },
  {
    slug: "products",
    title: "O que você vende?",
    description: "Adicione seu catálogo aqui. A IA precisa conhecer seus produtos, serviços e preços para poder vendê-los.",
    icon: "Package",
    route: "/products",
  },
  {
    slug: "agent",
    title: "Crie seu agente",
    description: "Crie e dê um nome ao seu assistente virtual de atendimento.",
    icon: "Bot",
    route: "/agents",
  },
  {
    slug: "agent_business",
    title: "Conte sobre seu negócio",
    description: "Preencha as informações da empresa, endereço e CNPJ na aba \"Negócio\" do agente.",
    icon: "Bot",
    route: "/agents",
  },
  {
    slug: "agent_faq",
    title: "Adicione perguntas frequentes",
    description: "Cadastre as dúvidas mais comuns dos seus clientes e as respostas ideais na aba \"Negócio\".",
    icon: "Bot",
    route: "/agents",
  },
  {
    slug: "agent_behavior",
    title: "Defina o comportamento",
    description: "Configure o tom de voz, idioma e as permissões do agente na aba \"Comportamento\".",
    icon: "Bot",
    route: "/agents",
  },
  {
    slug: "test_agent",
    title: "Hora do ensaio",
    description: "Simule uma conversa e teste as respostas da IA antes de colocar o atendimento no ar.",
    icon: "Beaker",
    route: "/agents-test",
  },
  {
    slug: "integrations",
    title: "Vamos pro ar!",
    description: "Integre o FabChat aos seus canais (WhatsApp, Instagram, Site) e ative a IA no mundo real.",
    icon: "Globe",
    route: "/integrations",
  },
  {
    slug: "dashboard",
    title: "Acompanhe os resultados",
    description: "É aqui que você vai monitorar o volume de mensagens, o desempenho da IA e o sucesso da sua operação.",
    icon: "BarChart3",
    route: "/dashboard",
  },
  {
    slug: "conversations",
    title: "Assuma o controle",
    description: "Aqui acontece a mágica. Alterne entre as conversas do WhatsApp e Instagram, ative ou pause a IA com um clique e gerencie os dados de cada cliente facilmente.",
    icon: "MessageSquare",
    route: "/conversations",
  },
  {
    slug: "appointments",
    title: "Gerencie sua agenda",
    description: "Acompanhe as reuniões marcadas automaticamente pela IA ou agende manualmente. Tudo fica 100% sincronizado com o seu Google Agenda!",
    icon: "Calendar",
    route: "/appointments",
  },
  {
    slug: "crm",
    title: "Não perca nenhuma venda",
    description: "Acompanhe a jornada do seu cliente. Mova contatos pelo funil de vendas, adicione anotações e tenha controle total sobre suas oportunidades.",
    icon: "Target",
    route: "/leads",
  },
  {
    slug: "conclusion",
    title: "Tudo pronto!",
    description: "Você finalizou as configurações básicas. Se precisar de tutoriais ou ajuda, nossa central fica aqui. Boas vendas!",
    icon: "PartyPopper",
    route: null,
  },
];

interface OnboardingContextType {
  steps: OnboardingStep[];
  completedSteps: string[];
  completeStep: (slug: string) => Promise<void>;
  progress: number;
  isOnboardingActive: boolean;
  showWelcome: boolean;
  dismissWelcome: () => Promise<void>;
  showChecklist: boolean;
  toggleChecklist: () => void;
  finishOnboarding: () => Promise<void>;
  navigateToStep: (step: OnboardingStep) => void;
  activeStepSlug: string | null;
  shownTips: string[];
  dismissTip: () => void;
  reopenOnboarding: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children, workspaceId, userId }: { children: ReactNode; workspaceId: string | null; userId: string | undefined }) {
  const [completedSteps, setCompletedSteps] = useState<string[]>(["account_created"]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeStepSlug, setActiveStepSlug] = useState<string | null>(null);
  const [shownTips, setShownTips] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { subscribed, productId, isWorkspaceMember } = useSubscription();
  const { isOwner: storeIsOwner, loading: workspaceLoading } = useWorkspace(userId);
  const checkedRoutesRef = useRef<Set<string>>(new Set());

  // Fonte única: usar apenas store para isOwner (evita reconsulta a workspace_members)
  useEffect(() => {
    if (!userId || !workspaceId) {
      setIsOwner(false);
      return;
    }
    if (workspaceLoading) {
      setIsOwner(false);
      return;
    }
    setIsOwner(storeIsOwner);
  }, [userId, workspaceId, workspaceLoading, storeIsOwner]);

  // Filter steps based on plan
  const isPlusPro = productId
    ? ["intermediario", "pro"].includes(getPlanByProductId(productId) || "")
    : false;

  const steps = ALL_STEPS.filter((s) => {
    if (s.visibleFor === "plus_pro") return isPlusPro;
    return true;
  });

  const isOnboardingActive = isOwner && subscribed && !isWorkspaceMember && !isCompleted && loaded;

  // Load onboarding state from DB
  useEffect(() => {
    if (!workspaceId || !userId || !isOwner) return;
    const load = async () => {
      // User-level dismissal — se o usuário já pulou/concluiu em qualquer workspace, não mostra mais.
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_dismissed_at")
        .eq("id", userId)
        .maybeSingle();
      const userDismissed = !!(profile as any)?.onboarding_dismissed_at;

      const { data } = await supabase
        .from("workspace_onboarding")
        .select("*")
        .eq("workspace_id", workspaceId)
        .maybeSingle();

      if (data) {
        const steps = (data.completed_steps as string[]) || ["account_created"];
        setCompletedSteps(steps);
        const skippedOrDone = userDismissed || data.is_completed || data.is_dismissed;
        setIsCompleted(skippedOrDone);
        setShowChecklist(!skippedOrDone && data.welcome_shown);
        if (!userDismissed && !data.welcome_shown && !skippedOrDone) {
          setShowWelcome(true);
        }
      } else {
        // Create record
        await supabase.from("workspace_onboarding").insert({
          workspace_id: workspaceId,
          user_id: userId,
          completed_steps: ["account_created"],
        });
        if (userDismissed) {
          setIsCompleted(true);
          setShowWelcome(false);
          setShowChecklist(false);
        } else {
          setShowWelcome(true);
        }
      }
      setLoaded(true);
    };
    load();
  }, [workspaceId, userId, isOwner]);

  // Auto-check navigation-based steps — only when navigated via checklist
  useEffect(() => {
    if (!isOnboardingActive || !activeStepSlug) return;
    const path = location.pathname;

    const routeStepMap: Record<string, string> = {
      "/agents-test": "test_agent",
      "/dashboard": "dashboard",
      "/conversations": "conversations",
      "/appointments": "appointments",
      "/agents": "agent_behavior",
    };

    const slug = routeStepMap[path];
    // Only complete if user arrived here via the checklist (activeStepSlug matches)
    if (slug && slug === activeStepSlug && !completedSteps.includes(slug)) {
      completeStep(slug);
    }

    // CRM: complete immediately when navigated via checklist
    if (path === "/leads" && activeStepSlug === "crm" && !completedSteps.includes("crm")) {
      completeStep("crm");
    }
  }, [location.pathname, isOnboardingActive, activeStepSlug]);

  // Auto-check data-based steps (ignoring pre-seeded sample data)
  // OPTIMIZED: Cache in sessionStorage, only re-check every 10 minutes
  useEffect(() => {
    if (!isOnboardingActive || !workspaceId) return;
    
    const CACHE_KEY = `fabchat_onboarding_data_check_${workspaceId}`;
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 10 * 60 * 1000) return; // Skip if checked <10 min ago
      } catch {}
    }

    const checkData = async () => {
      // Check leads: exclude sample leads (email ending in @example.com)
      if (!completedSteps.includes("leads")) {
        const { count } = await supabase
          .from("leads" as any)
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", workspaceId)
          .not("email", "like", "%@example.com");
        if (count !== null && count >= 1) {
          await completeStep("leads");
        }
      }

      // Check products: exclude sample products (SKU starting with PLAN-)
      if (!completedSteps.includes("products")) {
        const { count } = await supabase
          .from("products" as any)
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", workspaceId)
          .not("sku", "like", "PLAN-%");
        if (count !== null && count >= 1) {
          await completeStep("products");
        }
      }

      // Check agents: exclude default "Assistente de Vendas"
      if (!completedSteps.includes("agent")) {
        const { count } = await supabase
          .from("agents" as any)
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", workspaceId)
          .neq("name", "Assistente de Vendas");
        if (count !== null && count >= 1) {
          await completeStep("agent");
        }
      }

      // Check agent_business: agent has about_company filled
      if (!completedSteps.includes("agent_business")) {
        const { data: agentData } = await supabase
          .from("agents" as any)
          .select("about_company")
          .eq("workspace_id", workspaceId)
          .neq("name", "Assistente de Vendas")
          .not("about_company", "is", null)
          .neq("about_company", "")
          .limit(1)
          .maybeSingle();
        if (agentData) {
          await completeStep("agent_business");
        }
      }

      // Check agent_faq: agent has at least 1 FAQ item
      if (!completedSteps.includes("agent_faq")) {
        const { data: faqData } = await supabase
          .from("agents" as any)
          .select("faq_items")
          .eq("workspace_id", workspaceId)
          .neq("name", "Assistente de Vendas")
          .not("faq_items", "is", null)
          .limit(1)
          .maybeSingle();
        if (faqData && Array.isArray((faqData as any).faq_items) && (faqData as any).faq_items.length > 0) {
          await completeStep("agent_faq");
        }
      }

      // Check integrations (no sample data for this)
      if (!completedSteps.includes("integrations")) {
        const { count } = await supabase
          .from("integrations" as any)
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", workspaceId)
          .eq("status", "active");
        if (count !== null && count >= 1) {
          await completeStep("integrations");
        }
      }

      // Check members (only for plus/pro plans)
      if (isPlusPro && !completedSteps.includes("members")) {
        const { count } = await supabase
          .from("workspace_members" as any)
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", workspaceId);
        if (count !== null && count >= 2) {
          await completeStep("members");
        }
      }
      // Save cache timestamp
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now() }));
    };
    checkData();
  }, [location.pathname, isOnboardingActive, workspaceId]);

  const completeStep = useCallback(async (slug: string) => {
    setCompletedSteps((prev) => {
      if (prev.includes(slug)) return prev;
      const next = [...prev, slug];
      // Persist
      if (workspaceId) {
        supabase
          .from("workspace_onboarding")
          .update({ completed_steps: next })
          .eq("workspace_id", workspaceId)
          .then();
      }
      // Toast
      const stepDef = ALL_STEPS.find((s) => s.slug === slug);
      if (stepDef && slug !== "account_created") {
        toast({
          title: "🎉 Tarefa concluída!",
          description: `${stepDef.title} — feito!`,
        });
      }
      return next;
    });
  }, [workspaceId]);

  const dismissWelcome = useCallback(async () => {
    setShowWelcome(false);
    setShowChecklist(true);
    if (workspaceId) {
      await supabase
        .from("workspace_onboarding")
        .update({ welcome_shown: true })
        .eq("workspace_id", workspaceId);
    }
  }, [workspaceId]);

  const toggleChecklist = useCallback(() => {
    setShowChecklist((v) => !v);
  }, []);

  const finishOnboarding = useCallback(async () => {
    const wasShowingWelcome = showWelcome;
    setIsCompleted(true);
    setShowWelcome(false);
    setShowChecklist(false);
    if (workspaceId) {
      const { error } = await supabase
        .from("workspace_onboarding")
        .update({
          is_completed: true,
          is_dismissed: true,
          welcome_shown: true,
          completed_steps: [...completedSteps, "conclusion"],
        })
        .eq("workspace_id", workspaceId);
      if (error) {
        console.error("finishOnboarding:", error);
        setIsCompleted(false);
        setShowWelcome(wasShowingWelcome);
        setShowChecklist(!wasShowingWelcome);
        toast({
          title: "Não foi possível salvar",
          description: "Tente pular o tutorial novamente.",
          variant: "destructive",
        });
        return;
      }
    }
    // User-level dismissal (para não reaparecer em outros workspaces do mesmo usuário)
    if (userId) {
      await supabase
        .from("profiles")
        .update({ onboarding_dismissed_at: new Date().toISOString() } as any)
        .eq("id", userId);
    }
    toast({ title: "🎉 Tour finalizado!", description: "Boas vendas! Estamos aqui se precisar." });
  }, [workspaceId, userId, completedSteps, showWelcome]);

  const dismissTip = useCallback(() => {
    if (activeStepSlug) {
      setShownTips((prev) => [...prev, activeStepSlug]);
    }
    setActiveStepSlug(null);
  }, [activeStepSlug]);

  const reopenOnboarding = useCallback(async () => {
    setIsCompleted(false);
    setShowChecklist(true);
    if (workspaceId) {
      await supabase
        .from("workspace_onboarding")
        .update({ is_completed: false, is_dismissed: false })
        .eq("workspace_id", workspaceId);
    }
    if (userId) {
      await supabase
        .from("profiles")
        .update({ onboarding_dismissed_at: null } as any)
        .eq("id", userId);
    }
  }, [workspaceId, userId]);

  const navigateToStep = useCallback((step: OnboardingStep) => {
    if (step.route) {
      setActiveStepSlug(step.slug);
      // Reset tip so it reappears when navigating via checklist
      setShownTips((prev) => prev.filter((t) => t !== step.slug));
      navigate(step.route);
    }
  }, [navigate]);

  const progress = steps.length > 0
    ? Math.round((steps.filter((s) => completedSteps.includes(s.slug)).length / steps.length) * 100)
    : 0;

  return (
    <OnboardingContext.Provider
      value={{
        steps,
        completedSteps,
        completeStep,
        progress,
        isOnboardingActive,
        showWelcome,
        dismissWelcome,
        showChecklist,
        toggleChecklist,
        finishOnboarding,
        navigateToStep,
        activeStepSlug,
        shownTips,
        dismissTip,
        reopenOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used within OnboardingProvider");
  return ctx;
};
